/**
 * The rule a guided run is played by: does what the student just did satisfy
 * the step they are on?
 *
 * Pure, and deliberately separate from the bench that calls it — the whole
 * behaviour of guided mode is "accept this, refuse that, and say why", which
 * is worth being able to test without a browser.
 *
 * See docs/FeatureDesigns/chemistry-lab-guided-target.md.
 */
import type { GuidedProcedure, GuidedStep, Stoichiometry } from '@padcanvas/shared';
import { findSubstance, formatAmount } from './substanceCatalog';
import { formatMass, formatMoles, formatMolarity, formatVolume } from './quantityFormat';

/** What the student just tried to do, in the same vocabulary the script uses. */
export type BenchAttempt =
  /** `qty` is how much of that reagent the vessel holds AFTER this add, in the
   *  reagent's own unit — what a step asking for an amount is judged against.
   *  A second add of the same reagent is a bigger `qty`, not a second
   *  attempt. */
  | { kind: 'add-substance'; container: number; substanceId: string; qty: number }
  | { kind: 'dip-test'; container: number }
  | { kind: 'dip-rod'; container: number; substanceId: string }
  | { kind: 'attach-burner'; container: number }
  | { kind: 'connect-pipe'; container: number; toContainer: number }
  | { kind: 'swirl'; container: number }
  /** A rod carrying one reagent, held near another container's escaping gas
   *  — the ammonia/HCl "dense white fumes" test. No script vocabulary for
   *  this yet (see judge()'s switch, which discriminates on step.expect.kind
   *  and so needs no case added here), so it always refuses a guided step;
   *  free-form is unaffected — allow() never refuses there. */
  | { kind: 'rod-near-gas'; container: number; toContainer: number }
  /** Anything the script has no vocabulary for — placing a vessel, clearing a
   *  container, changing glassware. Never satisfies a step; refused with a
   *  reason of its own, since "the bench is already set out" is a different
   *  thing to say than "wrong reagent". */
  | { kind: 'off-script'; what: string };

export type GuidedVerdict = { ok: true } | { ok: false; message: string };

function substanceName(id: string): string {
  return findSubstance(id)?.displayName ?? id;
}

/**
 * What to say when a step is ignored and the author wrote no correction. Says
 * what to do, never only that something was wrong — see the target doc.
 */
export function defaultCorrection(step: GuidedStep): string {
  const e = step.expect;
  switch (e.kind) {
    case 'add-substance': return `This step needs ${substanceName(e.substanceId)}. ${step.instruction}`;
    case 'swirl': return `This step needs the vessel swirled to mix it. ${step.instruction}`;
    case 'dip-test': return `This step is the pH paper test. ${step.instruction}`;
    case 'dip-rod': return `This step needs the glass rod dipped in ${substanceName(e.substanceId)}. ${step.instruction}`;
    case 'attach-burner': return `This step needs a flame under the vessel. ${step.instruction}`;
    case 'heat': return `This step needs the mixture ${e.toBand === 'boiling' ? 'boiling' : 'hot'}. ${step.instruction}`;
    case 'connect-pipe': return `This step needs a delivery tube run between the two vessels. ${step.instruction}`;
    case 'observe': return step.instruction;
    case 'answer-quantity': return step.instruction;
  }
}

/**
 * What the bench itself worked out for the number a step is asking about.
 *
 * The single source: the step is checked against the working printed beside it,
 * so a student who reads the notebook and types what it says is never told they
 * are wrong. An activity that carried its own answer could disagree with the
 * bench the student is standing at, and the student would lose that argument.
 *
 * `null` where the working cannot answer — the species is not in the equation,
 * or there is no working at all — which the caller reports as a step that
 * cannot be asked rather than an answer that is wrong.
 */
export function benchAnswer(
  expect: Extract<GuidedStep['expect'], { kind: 'answer-quantity' }>,
  working: Stoichiometry | undefined,
): { value: number; formatted: string } | { formula: string } | null {
  if (!working) return null;
  const of = expect.of;
  const reagent = of ? working.reagents.find(r => r.formula === of) : undefined;
  const product = of ? working.products.find(p => p.formula === of) : undefined;

  switch (expect.quantity) {
    case 'limiting-reagent': {
      const limiting = working.reagents.find(r => r.limiting);
      return limiting ? { formula: limiting.formula } : null;
    }
    case 'moles':
      return reagent ? { value: reagent.mol, formatted: formatMoles(reagent.mol) } : null;
    case 'leftover-g':
      return reagent?.leftoverG != null
        ? { value: reagent.leftoverG, formatted: formatMass(reagent.leftoverG) }
        : null;
    case 'yield-mol':
      return product ? { value: product.mol, formatted: formatMoles(product.mol) } : null;
    case 'yield-g':
      return product?.massG != null
        ? { value: product.massG, formatted: formatMass(product.massG) }
        : null;
    case 'yield-volume-l':
      return product?.volumeL != null
        ? { value: product.volumeL, formatted: formatVolume(product.volumeL) }
        : null;
    case 'concentration':
      return product?.concentrationM != null
        ? { value: product.concentrationM, formatted: formatMolarity(product.concentrationM) }
        : null;
  }
}

/** The unit a step's answer is given in, so the field can say so and the
 *  student is not left guessing whether we want moles or millimoles. */
export function answerUnit(expect: Extract<GuidedStep['expect'], { kind: 'answer-quantity' }>): string {
  switch (expect.quantity) {
    case 'limiting-reagent': return 'formula';
    case 'moles': case 'yield-mol': return 'mmol';
    case 'leftover-g': case 'yield-g': return 'g';
    case 'yield-volume-l': return 'mL at RTP';
    case 'concentration': return 'M';
  }
}

/** The scale the student is typing in, against the SI the working holds in.
 *  A student writes 2.00 mmol; the bench holds 0.002 mol. */
function scaleOf(expect: Extract<GuidedStep['expect'], { kind: 'answer-quantity' }>): number {
  switch (expect.quantity) {
    case 'moles': case 'yield-mol': return 1000;      // mol → mmol
    case 'yield-volume-l': return 1000;               // L → mL
    default: return 1;
  }
}

/** Default closeness, percent. Enough for a student who rounded a molar mass,
 *  not enough for one who used the wrong ratio. */
const DEFAULT_TOLERANCE_PCT = 2;

/**
 * Is what the student typed the number the bench computed?
 *
 * A formula answer is compared as text, case-insensitively and ignoring
 * spaces: "caco3" is the right answer written by someone typing quickly, and
 * refusing it would be marking handwriting.
 */
export function judgeQuantity(
  step: GuidedStep,
  typed: string,
  working: Stoichiometry | undefined,
): GuidedVerdict {
  if (step.expect.kind !== 'answer-quantity') return { ok: false, message: step.instruction };
  const expect = step.expect;
  const answer = benchAnswer(expect, working);
  if (!answer) {
    // Not the student's fault, and not something they can fix by trying again.
    return { ok: false, message: 'The bench has not worked this one out — carry on, and we will come back to it.' };
  }

  const said = typed.trim();
  if (!said) return { ok: false, message: 'Type your answer first.' };

  if ('formula' in answer) {
    const normalise = (t: string) => t.replace(/\s+/g, '').toLowerCase();
    return normalise(said) === normalise(answer.formula)
      ? { ok: true }
      : { ok: false, message: `Not quite. Look at which reagent ran out first — it is the one with the smallest mol ÷ coefficient.` };
  }

  const value = Number(said.replace(/,/g, ''));
  if (!Number.isFinite(value)) {
    return { ok: false, message: `That is not a number. Give it in ${answerUnit(expect)}.` };
  }

  const expected = answer.value * scaleOf(expect);
  const tolerance = Math.abs(expected) * ((expect.tolerancePct ?? DEFAULT_TOLERANCE_PCT) / 100);
  if (Math.abs(value - expected) <= tolerance) return { ok: true };

  // Ten times out, either way, is a unit — the most common wrong answer that is
  // really a right one, and worth saying rather than marking.
  const ratio = expected === 0 ? 0 : value / expected;
  if (ratio >= 900 && ratio <= 1100) {
    return { ok: false, message: `Close — check your units. This wants ${answerUnit(expect)}.` };
  }
  if (ratio >= 0.0009 && ratio <= 0.0011) {
    return { ok: false, message: `Close — check your units. This wants ${answerUnit(expect)}.` };
  }
  return { ok: false, message: 'Not quite. Open the working under the last step and check it line by line.' };
}

/**
 * Whether `attempt` carries out `step`.
 *
 * The container is not checked. The step's vessel is selected for the student
 * when the step becomes current, so a mismatch here would only ever mean they
 * deliberately clicked elsewhere — and refusing "wrong beaker" is fussiness,
 * not teaching.
 */
export function judge(step: GuidedStep, attempt: BenchAttempt): GuidedVerdict {
  const refuse = { ok: false as const, message: step.correction ?? defaultCorrection(step) };
  if (attempt.kind === 'off-script') {
    return { ok: false, message: `The bench for this experiment is already set out — ${attempt.what} is not part of it. ${step.instruction}` };
  }
  const e = step.expect;
  switch (e.kind) {
    case 'add-substance': {
      if (attempt.kind !== 'add-substance' || attempt.substanceId !== e.substanceId) return refuse;
      // The right reagent, but not yet enough of it. That is a different thing
      // to say than "wrong reagent": the student is doing this step, and needs
      // to keep going rather than to change course.
      if (e.qty !== undefined && attempt.qty < e.qty) {
        const substance = findSubstance(e.substanceId);
        const wanted = substance ? formatAmount(substance, e.qty) : `${e.qty}`;
        return { ok: false, message: `Keep going — this step wants ${wanted} of ${substanceName(e.substanceId)}.` };
      }
      return { ok: true };
    }
    case 'swirl':
      return attempt.kind === 'swirl' ? { ok: true } : refuse;
    case 'dip-test':
      return attempt.kind === 'dip-test' ? { ok: true } : refuse;
    case 'dip-rod':
      return attempt.kind === 'dip-rod' && attempt.substanceId === e.substanceId ? { ok: true } : refuse;
    case 'attach-burner':
      return attempt.kind === 'attach-burner' ? { ok: true } : refuse;
    case 'connect-pipe':
      return attempt.kind === 'connect-pipe' && attempt.toContainer === e.toContainer ? { ok: true } : refuse;
    case 'heat':
      // Heat is reached, not performed: the burner slider is continuous and
      // crosses bands on the way to wherever it is going, so refusing each
      // crossing would fight the control. The step completes when the mixture
      // actually gets there — see reachedByHeat.
      return refuse;
    case 'observe':
      // Nothing on the bench satisfies an observe step; the panel's own
      // Continue button does. Anything else is a detour.
      return refuse;
    case 'answer-quantity':
      // Answered on the panel, in a field — see judgeQuantity. Nothing done to
      // the bench completes it, and pouring something else in would change the
      // very numbers being asked about.
      return refuse;
  }
}

/** Whether a settled burner reading completes a `heat` step. */
export function reachedByHeat(step: GuidedStep, band: 'room' | 'heated' | 'boiling'): boolean {
  if (step.expect.kind !== 'heat') return false;
  return step.expect.toBand === 'heated' ? band !== 'room' : band === 'boiling';
}

/** A step the student carries out on the panel, not on the bench. An
 *  `answer-quantity` step is also answered there, but it is not
 *  self-advancing: it advances on a correct answer, not on a click. */
export function isSelfAdvancing(step: GuidedStep): boolean {
  return step.expect.kind === 'observe';
}

/**
 * Which vessel the step acts on, clamped to the bench the experiment declared.
 *
 * Past the last step the run is finished, and it holds the LAST step's vessel
 * rather than falling back to the first. The final instruction is usually
 * "watch this one" — sending the reader back to vessel 1 the moment they
 * finish takes the result off the screen at exactly the moment it is the
 * point, which is what it did.
 */
export function stepContainer(experiment: GuidedProcedure, index: number): number {
  const step = experiment.steps[index] ?? experiment.steps[experiment.steps.length - 1];
  if (!step) return 0;
  return Math.min(Math.max(step.container, 0), experiment.bench.length - 1);
}
